import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./config/authConfig";
import { Navigation } from "./components/Navigation";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Loading } from "./components/Loading";
import { UnauthorizedPage } from "./components/UnauthorizedPage";
import { SessionExpiredModal } from "./components/SessionExpiredModal";
import { SchedulePage } from "./pages/SchedulePage";
import { ProjectPage } from "./pages/ProjectPage";
import AdminPage from "./pages/AdminPage";
import RedTeamPage from "./pages/RedTeamPage";
import { FounderPage } from "./pages/FounderPage";
import { TimesheetPage } from "./pages/TimesheetPage";
import { apiService } from "./services/apiService";
import { USER_ROLES } from "./config/authConfig";
import { hasAnyRole } from "./utils/authUtils";
import {
  ensureActiveMsalAccount,
  getPreferredMsalAccount,
} from "./utils/msalAccountUtils";
import { SearchProvider } from "./contexts/SearchContext";
import { GlobalSearchProvider } from "./contexts/GlobalSearchContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ActivitiesProvider } from "./contexts/ActivitiesContext";
import { PeopleProvider } from "./contexts/PeopleContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { AuthProvider } from "./contexts/AuthContext";
import { useApiAuth } from "./hooks/useApiAuth";

// PrimeReact CSS (theme will be loaded dynamically)
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { PrimeReactProvider } from "primereact/api";
import "./App.css";

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Set up event callbacks
msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as any;
    msalInstance.setActiveAccount(payload.account);

    // Set the ID token for API calls
    apiService.setIdToken(payload);
  } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
    msalInstance.setActiveAccount(null);
    apiService.setIdToken(null);
  }
});

const MsalWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();

        // Handle redirect responses exactly once during bootstrap.
        // This avoids re-processing redirects on subsequent renders.
        const response = await msalInstance.handleRedirectPromise?.();
        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
          apiService.setIdToken(response);
        }

        // Ensure we have an active account when a cached account exists.
        ensureActiveMsalAccount(
          msalInstance,
          msalInstance.getAllAccounts?.() ?? [],
        );

        setIsInitialized(true);
      } catch (error) {
        console.error("MSAL initialization failed:", error);
        setInitError(
          error instanceof Error
            ? error.message
            : "Authentication initialization failed.",
        );
      }
    };

    initializeMsal();
  }, []);

  // Initialize theme on app load
  useEffect(() => {
    const loadInitialTheme = () => {
      // Check localStorage for saved theme preference (system | light | dark)
      const pref = localStorage.getItem("themePreference");
      const systemPrefersDark =
        window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? false;
      const effectiveDark =
        pref === "dark" || pref === "system" || !pref
          ? systemPrefersDark
          : false;

      // Create or update theme link
      let linkElement = document.getElementById(
        "theme-link",
      ) as HTMLLinkElement;
      if (!linkElement) {
        linkElement = document.createElement("link");
        linkElement.id = "theme-link";
        linkElement.rel = "stylesheet";
        linkElement.type = "text/css";
        document.head.appendChild(linkElement);
      }

      // Set theme
      if (effectiveDark) {
        linkElement.href = `${process.env.PUBLIC_URL || ""}/themes/primereact/lara-dark-indigo/theme.css`;
        document.documentElement.classList.add("p-dark");
        document.body.classList.add("p-dark");
      } else {
        linkElement.href = `${process.env.PUBLIC_URL || ""}/themes/primereact/lara-light-indigo/theme.css`;
        document.documentElement.classList.remove("p-dark");
        document.body.classList.remove("p-dark");
      }

      // Notify listeners about initial theme state
      window.dispatchEvent(
        new CustomEvent("themechange", {
          detail: { isDarkMode: effectiveDark },
        }),
      );
    };

    loadInitialTheme();
  }, []);

  if (initError) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen surface-ground p-4">
        <div className="surface-card p-4 border-round shadow-2 w-full max-w-md text-center">
          <h2 className="text-900 mb-3">
            Authentication Initialization Failed
          </h2>
          <p className="text-600 mb-4">
            {initError}. Please reload the page to try again.
          </p>
          <button
            type="button"
            className="p-button p-component"
            onClick={() => window.location.reload()}
          >
            <span className="p-button-label p-c">Reload</span>
          </button>
        </div>
      </div>
    );
  }

  if (!isInitialized) {
    return <Loading message="Initializing authentication..." fullScreen />;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
};

const AppShell: React.FC<{
  account: ReturnType<typeof getPreferredMsalAccount>;
}> = ({ account }) => {
  const { hasAttemptedAuth } = useApiAuth();

  // Only block the shell until the first auth attempt finishes. Later silent
  // refreshes must not unmount the app, or an open schedule tab loses its state.
  if (!hasAttemptedAuth) {
    return <Loading message="Checking authentication status..." fullScreen />;
  }

  return (
    <ToastProvider>
      <GlobalSearchProvider>
        <SearchProvider>
          <ActivitiesProvider>
            <PeopleProvider>
              <NotificationsProvider>
                <Router>
                <div className="min-h-screen surface-ground">
                  <SessionExpiredModal />
                  {/* Only show navigation if user has any valid role */}
                  {hasAnyRole(account) && <Navigation />}
                  <main>
                    <Routes>
                      {/* Unauthorized route */}
                      <Route
                        path="/unauthorized"
                        element={<UnauthorizedPage />}
                      />

                      {/* Default redirect based on user authorization */}
                      <Route
                        path="/"
                        element={
                          hasAnyRole(account) ? (
                            <Navigate to="/schedule" replace />
                          ) : (
                            <Navigate to="/unauthorized" replace />
                          )
                        }
                      />

                      {/* Schedule page - accessible by Employee, Admin, and Founder roles */}
                      <Route
                        path="/schedule"
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              USER_ROLES.EMPLOYEE,
                              USER_ROLES.ADMIN,
                              USER_ROLES.FOUNDER,
                            ]}
                          >
                            <SchedulePage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Timesheet page - accessible by Employee, Admin, and Founder roles */}
                      <Route
                        path="/timesheet"
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              USER_ROLES.EMPLOYEE,
                              USER_ROLES.ADMIN,
                              USER_ROLES.FOUNDER,
                            ]}
                          >
                            <TimesheetPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin page - requires admin role */}
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute requiredRole={USER_ROLES.ADMIN}>
                            <AdminPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Red Team page — app RBAC red_team or infra_admin */}
                      <Route
                        path="/red-team"
                        element={
                          <ProtectedRoute
                            requiredAppRoles={[
                              USER_ROLES.RED_TEAM,
                              USER_ROLES.INFRA_ADMIN,
                            ]}
                          >
                            <RedTeamPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Founder page - requires founder role */}
                      <Route
                        path="/founder"
                        element={
                          <ProtectedRoute requiredRole={USER_ROLES.FOUNDER}>
                            <FounderPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Project page - accessible by Employee, Admin, and Founder roles */}
                      <Route
                        path="/project/:reference"
                        element={
                          <ProtectedRoute
                            requiredRoles={[
                              USER_ROLES.EMPLOYEE,
                              USER_ROLES.ADMIN,
                              USER_ROLES.FOUNDER,
                            ]}
                          >
                            <ProjectPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* 404 page */}
                      <Route
                        path="*"
                        element={
                          hasAnyRole(account) ? (
                            <div className="p-4">
                              <div className="text-center">
                                <h2>Page Not Found</h2>
                                <p>
                                  The page you're looking for doesn't exist.
                                </p>
                              </div>
                            </div>
                          ) : (
                            <Navigate to="/unauthorized" replace />
                          )
                        }
                      />
                    </Routes>
                  </main>
                </div>
                </Router>
              </NotificationsProvider>
            </PeopleProvider>
          </ActivitiesProvider>
        </SearchProvider>
      </GlobalSearchProvider>
    </ToastProvider>
  );
};

const AuthenticatedApp: React.FC = () => {
  const { instance, accounts, inProgress } = useMsal();
  const account = getPreferredMsalAccount(instance, accounts);

  // Keep active account consistent without initiating any interactive auth here.
  useEffect(() => {
    ensureActiveMsalAccount(instance, accounts);
  }, [accounts, instance]);

  // While MSAL is busy with an interaction, keep UI in a stable loading state.
  if (inProgress !== "none") {
    return <Loading message="Processing authentication..." fullScreen />;
  }

  return (
    <PrimeReactProvider>
      <AuthProvider>
        <AppShell account={account} />
      </AuthProvider>
    </PrimeReactProvider>
  );
};

const App: React.FC = () => {
  return (
    <MsalWrapper>
      <AuthenticatedApp />
    </MsalWrapper>
  );
};

export default App;
